// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { zodResolver } from '@hookform/resolvers/zod';
import { useDrawer } from '@shared/ui-kit/hooks/use-drawer';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useDocumentCatalog,
  type DocumentCatalogOption,
} from '../../hooks/use-document-catalog';
import {
  documentDrawerFiltersSchema,
  emptyDocumentDrawerFilters,
  type DocumentDrawerFilters,
  type DocumentScopeTab,
} from '../../types/document-filters';

export interface UseDocumentsFiltersDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly scope: DocumentScopeTab;
  readonly appliedFilters: DocumentDrawerFilters;
  readonly onApply: (filters: DocumentDrawerFilters) => void;
}

const withAllOption = (
  options: readonly DocumentCatalogOption[],
  label: string,
): DocumentCatalogOption[] => [{ value: '', label }, ...options];

export const useDocumentsFiltersDrawer = ({
  open,
  onClose,
  scope,
  appliedFilters,
  onApply,
}: UseDocumentsFiltersDrawerProps) => {
  const { t } = useTranslation();
  const { getCategoriesForScope, getSubcategoriesForCategory, languages } =
    useDocumentCatalog();
  const [DrawerRenderer, drawerControls] = useDrawer({ open, onClose });
  const wasOpenRef = useRef(false);

  const form = useForm<DocumentDrawerFilters>({
    resolver: zodResolver(documentDrawerFiltersSchema),
    defaultValues: emptyDocumentDrawerFilters(),
    mode: 'onChange',
  });

  const categoryValue = form.watch('category');

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      form.reset(appliedFilters);
    }

    wasOpenRef.current = open;
  }, [appliedFilters, form, open]);

  const categorySelectOptions = useMemo(
    () =>
      withAllOption(
        getCategoriesForScope(scope),
        t('documents.filters-drawer.all-categories', { ns: 'components' }),
      ),
    [getCategoriesForScope, scope, t],
  );

  const subcategorySelectOptions = useMemo(
    () =>
      withAllOption(
        categoryValue ? getSubcategoriesForCategory(scope, categoryValue) : [],
        t('documents.filters-drawer.all-subcategories', { ns: 'components' }),
      ),
    [categoryValue, getSubcategoriesForCategory, scope, t],
  );

  const languageSelectOptions = useMemo(
    () =>
      withAllOption(
        languages,
        t('documents.filters-drawer.all-languages', { ns: 'components' }),
      ),
    [languages, t],
  );

  const onCategoryChange = useCallback(
    (category: string) => {
      const subcategory = form.getValues('subcategory');

      if (
        subcategory &&
        !getSubcategoriesForCategory(scope, category).some(
          option => option.value === subcategory,
        )
      ) {
        form.setValue('subcategory', '', {
          shouldValidate: true,
        });
      }
    },
    [form, getSubcategoriesForCategory, scope],
  );

  const onClearDraft = useCallback(() => {
    form.reset(emptyDocumentDrawerFilters());
  }, [form]);

  const onViewResults = useCallback(
    (values: DocumentDrawerFilters) => {
      onApply(values);
      onClose();
    },
    [onApply, onClose],
  );

  return {
    t,
    DrawerRenderer,
    ...drawerControls,
    control: form.control,
    handleSubmit: form.handleSubmit,
    errors: form.formState.errors,
    isValid: form.formState.isValid,
    categorySelectOptions,
    subcategorySelectOptions,
    languageSelectOptions,
    isSubcategoryDisabled: !categoryValue || subcategorySelectOptions.length === 1,
    onCategoryChange,
    onClearDraft,
    onViewResults,
  };
};
